import { useState, useRef, useEffect, useCallback } from "react";
import StartingFive from "./StartingFive";
import Protocol from "./Protocol";
import SolariBoard from "./board/SolariBoard";
import HowItWorks from "./board/HowItWorks";
import About from "./About";
import Legal from "./Legal";
import { makeGeneratorName, hashStr } from "./nameEngine";


// ── Styles ──────────────────────────────────────────────────────────

const LOGO_SRC = '/titlelogo.webp';

const fonts = `
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 400; font-display: swap; src: url('/fonts/Barlow-Regular.woff2') format('woff2'); }
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 500; font-display: swap; src: url('/fonts/Barlow-Medium.woff2') format('woff2'); }
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 600; font-display: swap; src: url('/fonts/Barlow-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 400; font-display: swap; src: url('/fonts/BarlowCondensed-Regular.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 600; font-display: swap; src: url('/fonts/BarlowCondensed-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 700; font-display: swap; src: url('/fonts/BarlowCondensed-Bold.woff2') format('woff2'); }
@font-face {
  font-family: 'Eurostile';
  src: url('/Eurostile-Bold.woff2') format('woff2');
  font-weight: 700;
  font-display: swap;
        }
@font-face {
  font-family: 'RatedIcon';
  src: url('/RatedIcon.woff2') format('woff2');
  font-display: swap;
        }
`;


// ── App Component ───────────────────────────────────────────────────

export default function App() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [reroll, setReroll] = useState(0);
  const [showDNA, setShowDNA] = useState(false);
  const [phase, setPhase] = useState('input');
  const [revealStep, setRevealStep] = useState(0);
  const [view, setView] = useState('board');
  const cardRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (phase === 'input' && inputRef.current) inputRef.current.focus();
  }, [phase]);

  // The board and How It Works paint a near-black shell; every other view is
  // the generator's cream. Setting it on <body> is what stops the cream from
  // showing below the board and around its edges on short viewports.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const dark = view === 'board' || view === 'how' || view === 'protocol';
    document.body.classList.toggle('board-mode', dark);
    return () => document.body.classList.remove('board-mode');
  }, [view]);

  useEffect(() => {
    const applyHash = () => {
      const h = window.location.hash.replace('#', '');
      // #board/<leagueId> and #sleeper/<username> are both board routes;
      // useLeagueRoute reads the id or username out of the hash itself.
      // Anything unrecognized falls through to the board.
      const head = h.split('/')[0].split('?')[0];
      if (head === 'play') return setView('game');
      if (head === 'generate') return setView('home');
      if (head === 'about' || head === 'legal') return setView(head);
      if (head === 'how') return setView('how');
      if (head === 'protocol') return setView('protocol');
      return setView('board');
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  const go = (v) => { window.location.hash = ({ game: 'play', about: 'about', legal: 'legal', home: 'generate', board: 'board', how: 'how', protocol: 'protocol' })[v]; };

  const handleGenerate = useCallback(() => {
    if (!input.trim()) return;
    const r = makeGeneratorName(input, reroll);
    setResult(r);
    setShowDNA(false);
    setRevealStep(0);
    setPhase('reveal');
    setTimeout(() => setRevealStep(1), 400);
    setTimeout(() => setRevealStep(2), 900);
    setTimeout(() => setPhase('card'), 1300);
  }, [input, reroll]);

  const handleReroll = () => {
    const next = reroll + 1;
    setReroll(next);
    const r = makeGeneratorName(input, next);
    setResult(r);
    setShowDNA(false);
    setRevealStep(0);
    setPhase('reveal');
    setTimeout(() => setRevealStep(1), 400);
    setTimeout(() => setRevealStep(2), 900);
    setTimeout(() => setPhase('card'), 1300);
  };

  const handleReset = () => { setInput(''); setResult(null); setReroll(0); setShowDNA(false); setPhase('input'); };

  const handleShare = async () => {
    if (!result) return;
    const text = `My Buttfumbles name is ${result.name}! 🏆\n\nAura: ${result.sources.map(s => `${s.pct}% ${s.name}`).join(' + ')}\n\nGet yours at buttfumbles.com`;
    if (navigator.share) { try { await navigator.share({ text }); } catch {} }
    else { await navigator.clipboard.writeText(text); alert('Copied to clipboard!'); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleGenerate(); };

  const positions = ['RF','LF','CF','SS','2B','3B','1B','C','SP','QB','WR','RB','TE','PG','SG','SF','PF','RW','LW','D','G'];
  const pos = positions[hashStr((result?.name || '') + 'pos') % positions.length];
  const num = (hashStr((result?.name || '') + 'num') % 89) + 1;

  if (view === 'board') return <SolariBoard />;
  if (view === 'how') return <HowItWorks onExit={() => go('board')} />;
  if (view === 'game') return <StartingFive onExit={() => go('home')} />;
  if (view === 'protocol') return <Protocol onExit={() => go('board')} />;
  if (view === 'about') return <About onExit={() => go('home')} onPlay={() => go('game')} />;
  if (view === 'legal') return <Legal onExit={() => go('home')} />;

  return (
    <>
      <style>{fonts}{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        /* The generator's cream is scoped to .app. The board paints its own
           near-black shell, and a cream body bled past it at the bottom and
           edges. body follows whichever view is mounted via .board-mode. */
        body, html { min-height: 100vh; overflow-x: hidden; background: #f5f4f2; }
        body.board-mode { background: #07080a; }
        .app { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 24px 16px; background: #f5f4f2; font-family: 'Barlow', sans-serif; color: #1a1a2e; }
        .title-logo { width: clamp(160px, 42vw, 240px); height: auto; margin-bottom: 12px; border-radius: 12px; }
        .subtitle { font-family: 'Barlow Condensed', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #999; text-align: center; margin-bottom: 32px; }
        .input-area { display: flex; flex-direction: column; align-items: center; gap: 14px; width: 100%; max-width: 400px; animation: fadeUp 0.5s ease-out; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .input-field { width: 100%; padding: 14px 18px; font-family: 'Barlow Condensed', sans-serif; font-size: 22px; font-weight: 600; letter-spacing: 1px; text-align: center; background: #fff; border: 2px solid #ddd; border-radius: 8px; color: #1a1a2e; outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
        .input-field::placeholder { color: #bbb; font-weight: 400; }
        .input-field:focus { border-color: #e7589b; box-shadow: 0 0 0 3px rgba(231,88,155,0.15); }
        .generate-btn { padding: 12px 44px; font-family: 'Barlow Condensed', sans-serif; font-size: 17px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; background: #e7589b; color: #fff; border: none; border-radius: 8px; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s, background 0.15s; }
        .generate-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(231,88,155,0.35); background: #d44d8b; }
        .generate-btn:active { transform: translateY(0); }
        .generate-btn:disabled { opacity: 0.3; cursor: default; transform: none; box-shadow: none; }
        .input-hint { font-family: 'Barlow', sans-serif; font-size: 13px; color: #aaa; text-align: center; }
        .sf-launch { margin-top: 10px; padding: 10px 26px; font-family: 'Barlow Condensed', sans-serif; font-size: 15px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; background: #fff; color: #77b2ee; border: 2px solid #77b2ee; border-radius: 8px; cursor: pointer; transition: all 0.15s; }
        .sf-launch:hover { background: #77b2ee; color: #fff; transform: translateY(-2px); box-shadow: 0 4px 16px rgba(119,178,238,0.35); }
        .home-footer { margin-top: 28px; display: flex; align-items: center; gap: 10px; }
        .home-link { background: none; border: none; font-family: 'Barlow Condensed', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #bbb; cursor: pointer; padding: 4px; transition: color 0.15s; }
        .home-link:hover { color: #e7589b; }
        .home-dot { color: #ddd; }
        .card-container { perspective: 1000px; animation: cardIn 0.7s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes cardIn { from { opacity: 0; transform: scale(0.85) rotateX(8deg); } to { opacity: 1; transform: scale(1) rotateX(0); } }
        .card { width: 340px; max-width: 90vw; background: #f2f0ef; border-radius: 10px; overflow: hidden; position: relative; box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08); }
        .card-header { background: #e7589b; padding: 10px 18px; display: flex; justify-content: space-between; align-items: center; }
        .card-header-text { font-family: 'Barlow Condensed', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #fff; }
        .card-number { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 18px; color: rgba(255,255,255,0.9); }
        .card-body { padding: 24px 20px 18px; text-align: center; position: relative; }
        .card-position { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 13px; letter-spacing: 4px; text-transform: uppercase; color: #e7589b; margin-bottom: 10px; }
        .card-name { font-family: 'Eurostile', 'Impact', sans-serif; font-weight: 700; font-size: clamp(26px, 7vw, 36px); color: #fff603; line-height: 1.1; margin-bottom: 2px; transform: rotate(-2.5deg); -webkit-text-stroke: 2px #000; text-shadow: 2px 2px 0 #000, 3px 3px 0 #000, 4px 4px 0 rgba(0,0,0,0.6), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000; paint-order: stroke fill; }
        .card-name-last { font-family: 'Eurostile', 'Impact', sans-serif; font-weight: 700; font-size: clamp(30px, 8vw, 42px); color: #fff603; line-height: 1.0; letter-spacing: 1px; transform: rotate(-2.5deg); -webkit-text-stroke: 2px #000; text-shadow: 2px 2px 0 #000, 3px 3px 0 #000, 4px 4px 0 rgba(0,0,0,0.6), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000; paint-order: stroke fill; margin-bottom: 14px; }
        .card-divider { width: 50px; height: 2px; background: #ddd; margin: 0 auto 10px; }
        .card-tagline { font-family: 'Barlow', sans-serif; font-size: 12px; color: #999; letter-spacing: 1px; }
        .rated-icon { font-family: 'RatedIcon', sans-serif; font-size: 44px; color: #222; position: absolute; bottom: 16px; right: 14px; z-index: 3; line-height: 1; opacity: 0.7; }
        .card-footer { background: #77b2ee; padding: 8px 18px; text-align: center; }
        .card-footer-text { font-family: 'Barlow Condensed', sans-serif; font-size: 10px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #fff; }
        .dna-panel { width: 340px; max-width: 90vw; margin-top: 14px; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: 18px; animation: fadeUp 0.4s ease-out; }
        .dna-title { font-family: 'Barlow Condensed', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #999; margin-bottom: 10px; text-align: center; }
        .dna-initials { font-family: 'Barlow Condensed', sans-serif; font-size: 12px; letter-spacing: 1px; color: #999; text-align: center; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #eee; }
        .dna-initials-highlight { color: #e7589b; font-weight: 700; }
        .dna-source { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .dna-bar-bg { flex: 1; height: 5px; background: #f0f0f0; border-radius: 3px; overflow: hidden; }
        .dna-bar { height: 100%; border-radius: 3px; transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1); }
        .dna-bar-1 { background: #e7589b; }
        .dna-bar-2 { background: #77b2ee; }
        .dna-bar-3 { background: #444; }
        .dna-name { font-family: 'Barlow Condensed', sans-serif; font-weight: 600; font-size: 14px; color: #333; min-width: 130px; }
        .dna-pct { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 14px; color: #e7589b; min-width: 34px; text-align: right; }
        .actions { display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap; justify-content: center; }
        .action-btn { padding: 9px 20px; font-family: 'Barlow Condensed', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; border: 1.5px solid #ddd; background: #fff; color: #666; border-radius: 6px; cursor: pointer; transition: all 0.15s; }
        .action-btn:hover { background: #f5f5f5; border-color: #ccc; color: #333; }
        .action-btn.primary { background: #e7589b; color: #fff; border-color: #e7589b; }
        .action-btn.primary:hover { background: #d44d8b; box-shadow: 0 3px 12px rgba(231,88,155,0.3); }
        .reveal-container { display: flex; flex-direction: column; align-items: center; gap: 20px; }
        .reveal-text { font-family: 'Barlow Condensed', sans-serif; font-size: 13px; letter-spacing: 4px; text-transform: uppercase; color: #aaa; animation: pulse 1.2s ease-in-out infinite; }
        @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
      `}</style>
      <div className="app">
        {phase === 'input' && (<>
          <img src={LOGO_SRC} alt="Buttfumbles" className="title-logo" />
          <div className="subtitle">Fake Athlete Name Generator</div>
          <div className="input-area">
            <input ref={inputRef} className="input-field" type="text" placeholder="Enter any name" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} />
            <button className="generate-btn" onClick={handleGenerate} disabled={!input.trim()}>Generate</button>
            <div className="input-hint">Type your name, a friend's name, or any name</div>
            <button className="sf-launch" onClick={() => go('game')}>Play Starting Five →</button>
          </div>
          <div className="home-footer">
            <button className="home-link" onClick={() => go('board')}>Board</button>
            <span className="home-dot">·</span>
            <button className="home-link" onClick={() => go('about')}>About</button>
            <span className="home-dot">·</span>
            <button className="home-link" onClick={() => go('legal')}>Privacy</button>
          </div>
        </>)}
        {phase === 'reveal' && (
          <div className="reveal-container">
            <img src={LOGO_SRC} alt="Buttfumbles" className="title-logo" />
            <div className="reveal-text">
              {revealStep === 0 && 'Consulting the scouting reports...'}
              {revealStep === 1 && 'Cross-referencing the rosters...'}
              {revealStep === 2 && 'Filing the paperwork...'}
            </div>
          </div>
        )}
        {phase === 'card' && result && (<>
          <div className="card-container">
            <div className="card" ref={cardRef}>
              <div className="card-header">
                <span className="card-header-text">Buttfumbles</span>
                <span className="card-number">#{String(num).padStart(2, '0')}</span>
              </div>
              <div className="card-body">
                <div className="card-position">{pos}</div>
                <div className="card-name">{result.name.split(' ')[0]}</div>
                <div className="card-name-last">{result.name.split(' ').slice(1).join(' ')}</div>
                <div className="card-divider" />
                <div className="card-tagline">Drafted: {2020 + (hashStr(result.name) % 6)}</div>
                <span className="rated-icon">{'\uE800'}</span>
              </div>
              <div className="card-footer">
                <span className="card-footer-text">buttfumbles.com</span>
              </div>
            </div>
          </div>
          {showDNA && (
            <div className="dna-panel">
              <div className="dna-title">Athlete Aura Makeup</div>
              <div className="dna-initials">
                {result.fullChaos
                  ? <><span className="dna-initials-highlight">FULL CHAOS</span> — no initial match</>
                  : <>Initial match: <span className="dna-initials-highlight">{input.trim()[0].toUpperCase()}.</span> → {result.name.split(' ')[0][0]}.</>
                }
              </div>
              {result.sources.map((s, i) => (
                <div className="dna-source" key={i}>
                  <span className="dna-pct">{s.pct}%</span>
                  <div className="dna-bar-bg"><div className={`dna-bar dna-bar-${i + 1}`} style={{ width: s.pct + '%' }} /></div>
                  <span className="dna-name">{s.name}</span>
                </div>
              ))}
            </div>
          )}
          <div className="actions">
            <button className="action-btn" onClick={() => setShowDNA(!showDNA)}>{showDNA ? 'Hide' : 'Show'} Aura</button>
            <button className="action-btn" onClick={handleReroll}>Re-roll</button>
            <button className="action-btn primary" onClick={handleShare}>Share</button>
            <button className="action-btn" onClick={handleReset}>New Name</button>
          </div>
        </>)}
      </div>
    </>
  );
}
